import { ChannelSnapshot } from "../domain/channelPositionDiff.js";

export type LogEntry = {
    id: string;
    createdAt: number;
    extra?: Record<string, any>;
};

export enum ChannelType {
    Text = 0,
    Voice = 2,
    Category = 4,
};

export interface Channel {
    id: string;
    name: string;
    type: ChannelType;
    position: number;
    parentId: string | null;
}

export type ChannelCategoryMap = Map<string | null, Channel[]>;

export async function fetchAndOrganizeChannels(): Promise<ChannelCategoryMap> {
    const res = await fetch("/api/channels");

    if (!res.ok) {
        throw new Error(`Failed to fetch channels (${res.status})`);
    }

    const channels: Channel[] = await res.json();
    return reorganizeChannelsIntoCategories(channels);
}

export async function fetchClusterRange(start: number, end: number): Promise<LogEntry[]> {
    const all: LogEntry[] = [];
    let after = start;
    let done = false;

    while (!done) {
        const logs: LogEntry[] = await fetch(
            `/api/logs?after=${after}&limit=25&orderBy=ASC`,
            {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({
                    types: ["channel_position_update"],
                }),
            }
        )
            .then((res) => res.json())
            .then((res) => res.result ?? []);

        if (logs.length === 0)
            break;

        for (const log of logs) {
            if (log.createdAt > end) {
                done = true;
                break;
            }

            all.push(log);
        }

        if (logs.length < 25)
            break;

        after = logs.at(-1)!.createdAt;
    }

    return all;
}

export function reorganizeChannelsIntoCategories(channels: Channel[]): ChannelCategoryMap {
    const organizedChannels: ChannelCategoryMap = new Map();

    // Copie défensive + tri déterministe
    const sorted = [...channels].sort((a, b) => {
        if (a.type !== b.type) {
            return a.type - b.type;
        }

        if (a.position !== b.position) {
            return a.position - b.position;
        }

        // fallback stable (Discord IDs = snowflakes)
        return Number(BigInt(a.id) - BigInt(b.id));
    });

    for (const channel of sorted) {
        const categoryId = channel.parentId ?? null;

        // Init bucket parent
        if (!organizedChannels.has(categoryId)) {
            organizedChannels.set(categoryId, []);
        }

        // Init bucket catégorie elle-même
        if (channel.type === ChannelType.Category && !organizedChannels.has(channel.id)) {
            organizedChannels.set(channel.id, []);
        }

        organizedChannels.get(categoryId)!.push(channel);
    }

    return organizedChannels;
}

export function buildChannelSnapshotMap(channels: Channel[]): Map<string, ChannelSnapshot> {
    const map = new Map<string, ChannelSnapshot>();

    for (const channel of channels) {
        map.set(channel.id, {
            id: channel.id,
            name: channel.name,
            position: channel.position,
            parentId: channel.parentId ?? null,
        });
    }

    return map;
}

export function rollbackChannelState(
    current: Map<string, ChannelSnapshot>,
    clusterLogs: LogEntry[]
): Map<string, ChannelSnapshot> {
    const rolledBack = structuredClone(current);

    for (const log of clusterLogs) {
        const id = log?.extra?.["channel"]?.["id"];
        if (!id) continue;

        const channel = rolledBack.get(id);
        if (!channel) continue;

        channel.position = log.extra?.["oldPosition"] ?? log.extra?.["newPosition"];
        channel.parentId = log.extra?.["oldParentId"] ?? log.extra?.["newParentId"] ?? null;
    }

    return rolledBack;
}
